'use client'

import React, { useState, useCallback, useEffect } from 'react'
import {
  X, Globe, Monitor, Smartphone, Calendar, User, AlertCircle,
  ImageOff, AlertTriangle, RefreshCw, Maximize2, Minimize2,
  Phone, Tag, Loader2, Clock, Activity,
} from 'lucide-react'

// ─── public types ─────────────────────────────────────────────────────────────

export interface RawEntry {
  id: string
  createdAt: string
  postType?: string | null
  headline?: string | null
  title?: string | null
  processedInputDescription?: string | null
  messageContent?: string | null
  sender?: string | null
  platformName?: string | null
  platformNameCode?: string | null
  isScam?: boolean
  articleUrl?: string | null
  userEntryDomainId?: string | null
  domainCreatedAt?: string | null
  lastMobileIsAlive?: boolean | null
  lastDesktopIsAlive?: boolean | null
  rawDomainMetadata?: {
    createDate?: string | null
    domain?: string | null
    originalUrl?: string | null
    [key: string]: any
  } | null
  /** Explicit domain active override (e.g. brand campaigns use is_active directly) */
  domainActive?: boolean
  /** Explicit type override (e.g. 'Marca' for brand campaigns) */
  type?: string
}

export interface Finding {
  title: string
  description: string
  severity: 'high' | 'medium'
}

export interface ThreatDetailLabels {
  findingsTitle: string
  screenshotsTitle: string
  tagsTitle: string
  timelineTitle: string
  domainCreatedLabel: string
  reportedLabel: string
  firstCaptureLabel: string
  mitigatedLabel: string
}

export interface ThreatDetailConfig {
  /** API base path prefix — '' for CTT sub-app, '/backoffice' for backoffice */
  apiBase?: string
  /** Override the findings logic for this project */
  getFindings?: (entry: RawEntry, browserMeta: any) => Finding[]
  /** Override individual UI labels */
  labels?: Partial<ThreatDetailLabels>
}

export interface ThreatDetailModalProps {
  entry: RawEntry | null
  /** Override domainCreatedAt when it comes from a separate source (e.g. backoffice live fetch) */
  domainCreatedAt?: string | null
  onClose: () => void
  /** Returns a fresh Turnstile token each call (caller manages the widget) */
  getToken: () => Promise<string>
  config?: ThreatDetailConfig
}

// ─── defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_LABELS: ThreatDetailLabels = {
  findingsTitle: 'Por que é suspeito?',
  screenshotsTitle: 'Pré-visualização do website',
  tagsTitle: 'Categorias',
  timelineTitle: 'Linha do Tempo',
  domainCreatedLabel: 'Domínio criado',
  reportedLabel: 'Reportado',
  firstCaptureLabel: 'Primeira recolha',
  mitigatedLabel: 'Mitigado',
}

// ─── inline derivation helpers ─────────────────────────────────────────────────

function deriveSourceType(postType: string | null | undefined): 'domain' | 'scall' | 'entity' {
  if (postType === 'call') return 'scall'
  return 'domain'
}

function deriveChannel(postType: string | null | undefined, platformName: string | null | undefined): string {
  if (postType === 'call') return 'Telefone'
  if (postType === 'url' || postType === 'image') return 'Web'
  if (postType === 'text' && !platformName) return 'SMS'
  const p = platformName?.toLowerCase() ?? ''
  if (p.includes('whatsapp')) return 'WhatsApp'
  if (p.includes('sms')) return 'SMS'
  if (p.includes('email') || p.includes('mail')) return 'Email'
  if (p.includes('facebook') || p.includes('instagram') || p.includes('twitter') || p.includes('tiktok')) return 'Redes sociais'
  return 'Web'
}

// ─── default findings (CTT) ───────────────────────────────────────────────────

export function getCttFindings(entry: RawEntry, browserMeta: any): Finding[] {
  const findings: Finding[] = []
  const sourceType = deriveSourceType(entry.postType)
  const domain = sourceType === 'domain'
    ? (entry.articleUrl ?? entry.rawDomainMetadata?.originalUrl ?? entry.rawDomainMetadata?.domain ?? null)
    : null
  const channel = deriveChannel(entry.postType, entry.platformName)
  const type = entry.type ?? (!entry.isScam ? 'Outro' : sourceType === 'scall' ? 'Vishing' : channel === 'SMS' || channel === 'WhatsApp' ? 'Smishing' : 'Phishing')
  const domainActive = entry.domainActive ?? (entry.lastMobileIsAlive === true || entry.lastDesktopIsAlive === true)

  if (browserMeta?.securityFindings?.length || browserMeta?.behavioralDifferences?.length) {
    const sev = (s: string): 'high' | 'medium' =>
      s === 'critical' || s === 'high' ? 'high' : 'medium'
    browserMeta.securityFindings?.forEach((f: any) => findings.push({
      title: f.type?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Problema de Segurança',
      description: f.description || 'Preocupação de segurança detectada.',
      severity: sev(f.severity?.toLowerCase() ?? ''),
    }))
    browserMeta.behavioralDifferences?.forEach((d: any) => findings.push({
      title: d.type?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Comportamento Suspeito',
      description: d.description || 'Comportamento suspeito detectado.',
      severity: sev(d.severity?.toLowerCase() ?? ''),
    }))
    if (findings.length > 0)
      return findings.sort((a, b) => (a.severity === 'high' && b.severity !== 'high' ? -1 : 1))
  }

  if (sourceType === 'domain') {
    if (domainActive)
      findings.push({ title: 'Domínio activo e operacional', severity: 'high',
        description: `O domínio "${domain}" continua activo e pode estar a recolher dados de vítimas neste momento.` })
    else if (domain)
      findings.push({ title: 'Domínio fraudulento identificado', severity: 'medium',
        description: `O domínio "${domain}" foi identificado como fraudulento. O takedown foi accionado.` })
    if (type === 'Phishing')
      findings.push({ title: 'Engenharia social — recolha de credenciais', severity: 'high',
        description: 'A mensagem recorre a urgência e imitação de fontes legítimas para obter credenciais ou dados sensíveis.' })
  }

  if (sourceType === 'scall')
    findings.push({ title: 'Chamada de voz fraudulenta (Vishing)', severity: 'high',
      description: 'O número reportado está associado a campanhas de vishing. Nunca forneça dados pessoais por telefone sem verificação.' })

  if (sourceType === 'entity')
    findings.push({ title: 'Referência ou entidade não autorizada', severity: 'high',
      description: 'A referência ou entidade apresentada não corresponde a qualquer cobrança ou comunicação legítima dos CTT.' })

  if (findings.length === 0)
    findings.push({ title: 'Mensagem suspeita reportada', severity: 'medium',
      description: 'Esta ocorrência foi reportada como suspeita e está em análise pela nossa equipa de segurança.' })

  return findings.sort((a, b) => (a.severity === 'high' && b.severity !== 'high' ? -1 : 1))
}

// ─── internal types ───────────────────────────────────────────────────────────

interface HistoryEntry {
  id: string
  createdAt: string
  r2Key: string
  hasImage: boolean
  desktopIsAlive: boolean | null
  mobileIsAlive: boolean | null
  rawBrowserMetadata?: any
}

interface ScreenshotData {
  desktop?: string | null
  mobile?: string | null
}

interface ProbeRun {
  id: string
  created_at: string
  probe_node: string | null
  triggered_by?: string | null
}

interface ProbeResult {
  id: string
  probe_layer: string
  test_type: 'dns' | 'tcp' | 'tls'
  is_alive: boolean
  raw_result: any
}

interface ProbeData {
  run: ProbeRun
  results: ProbeResult[]
}

// ─── helpers ──────────────────────────────────────────────────────────────────

const fmt = (v: string | Date, includeYear = true): string => {
  const d = v instanceof Date ? v : new Date(v)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return includeYear ? `${dd}/${mm}/${yyyy}` : `${dd}/${mm}`
}

const fmtDuration = (ms: number): string => {
  const h = ms / 3_600_000
  if (h < 1) return `${Math.round(ms / 60_000)}m`
  if (h < 24) return `${Math.round(h)}h`
  const d = Math.floor(h / 24)
  const r = Math.round(h % 24)
  return r === 0 ? `${d}d` : `${d}d ${r}h`
}

const extractScreenshots = (decData: any): ScreenshotData | null => {
  const scenarios = decData?.data?.buffer?.scenarios
  if (!scenarios) return null
  const out: ScreenshotData = {}
  if (scenarios.desktop?.screenshots?.[0]?.data)
    out.desktop = `data:image/png;base64,${scenarios.desktop.screenshots[0].data}`
  if (scenarios.mobile?.screenshots?.[0]?.data)
    out.mobile = `data:image/png;base64,${scenarios.mobile.screenshots[0].data}`
  return Object.keys(out).length > 0 ? out : null
}

// ─── sub-components ───────────────────────────────────────────────────────────

function BrowserWindow({
  url, imageSrc, deviceType, loadSuccess, statusCode, isLoading, isExpanded, onToggleExpand,
}: {
  url: string
  imageSrc?: string | null
  deviceType: 'desktop' | 'mobile'
  loadSuccess?: boolean
  statusCode?: number
  isLoading: boolean
  isExpanded: boolean
  onToggleExpand: () => void
}) {
  const isMobile = deviceType === 'mobile'
  const DeviceIcon = isMobile ? Smartphone : Monitor

  return (
    <div className={`overflow-hidden ${isMobile
      ? 'rounded-[28px] border-[7px] border-gray-900 bg-gray-900 shadow-lg sm:max-w-[280px] sm:mx-auto w-full'
      : 'rounded-lg border border-border bg-white shadow-sm'
    }`}>
      {isMobile ? (
        <div className="flex flex-col gap-2 bg-gray-900 px-3 pb-3 pt-2">
          <div className="flex justify-center">
            <div className="h-1 w-14 rounded-full bg-gray-700" />
          </div>
          <div className="flex items-center gap-1.5 rounded-full border border-gray-700 bg-gray-800 px-3 py-1.5">
            <Globe className="h-2.5 w-2.5 shrink-0 text-gray-400" />
            <span className="flex-1 truncate text-[9px] text-gray-400">{url}</span>
            <button onClick={onToggleExpand} className="text-gray-500 hover:text-gray-300">
              {isExpanded ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 border-b border-border bg-gray-100 px-3 py-2">
          <div className="flex gap-1.5">
            <div className="h-3 w-3 rounded-full bg-[#FF5F57]" />
            <div className="h-3 w-3 rounded-full bg-[#FFBD2E]" />
            <div className="h-3 w-3 rounded-full bg-[#28CA42]" />
          </div>
          <div className="flex flex-1 items-center gap-2 rounded border border-border bg-white px-2.5 py-1">
            <Globe className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="truncate text-[10px] text-muted-foreground">{url}</span>
          </div>
          <button onClick={onToggleExpand} className="p-1 text-muted-foreground hover:text-foreground">
            {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
        </div>
      )}

      <div className={isMobile ? 'overflow-hidden rounded-[16px] bg-white' : 'bg-gray-50'}>
        {isLoading ? (
          <div className="flex h-64 flex-col items-center justify-center gap-3 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            <span className="text-xs">A carregar captura...</span>
          </div>
        ) : imageSrc ? (
          <div className={`relative overflow-y-auto ${isExpanded ? 'max-h-[600px]' : 'max-h-[400px]'}`}>
            <img src={imageSrc} alt={`${deviceType} screenshot`} className="w-full" />
            <div className="absolute bottom-2 right-2 flex items-center gap-1.5 rounded border border-border bg-white/95 px-2 py-1 shadow-sm">
              <DeviceIcon className="h-3 w-3 text-muted-foreground" />
              <span className="text-[9px] text-muted-foreground">
                {deviceType === 'desktop' ? 'Desktop' : 'Mobile'}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex h-48 flex-col items-center justify-center gap-3 px-4 text-center text-muted-foreground">
            <DeviceIcon className="h-12 w-12 stroke-[1.5]" />
            <p className="text-xs">
              {loadSuccess === false
                ? `O website não carregou${statusCode ? ` (${statusCode})` : ''}`
                : 'Captura não disponível'}
            </p>
          </div>
        )}
      </div>

      {isMobile && (
        <div className="flex h-5 items-center justify-center bg-gray-900">
          <div className="h-1 w-20 rounded-full bg-gray-700" />
        </div>
      )}
    </div>
  )
}

function CampaignTimeline({ createdAt, imageHistory, domainCreatedAt, labels }: {
  createdAt: string
  imageHistory: HistoryEntry[]
  domainCreatedAt?: string | null
  labels: ThreatDetailLabels
}) {
  const asc = [...imageHistory].reverse()
  const firstActiveIdx = asc.findIndex(h => h.desktopIsAlive === true || h.mobileIsAlive === true)
  const firstActive = firstActiveIdx >= 0 ? asc[firstActiveIdx] : null
  const takedownEntry = firstActive
    ? asc.slice(firstActiveIdx + 1).find(h => h.desktopIsAlive === false && h.mobileIsAlive === false)
    : null
  const takedownDate = takedownEntry ? new Date(takedownEntry.createdAt) : null
  const takedownDuration = takedownDate && firstActive
    ? fmtDuration(takedownDate.getTime() - new Date(firstActive.createdAt).getTime())
    : null

  type Ev = { label: string; date: Date; color: string; sub?: string; subColor?: string }
  const events: Ev[] = []

  if (domainCreatedAt) {
    events.push({ label: labels.domainCreatedLabel, date: new Date(domainCreatedAt), color: '#94A3B8' })
  }

  events.push({ label: labels.reportedLabel, date: new Date(createdAt), color: '#EF4444' })

  if (asc.length > 0) {
    const first = asc[0]
    const anyAlive = first.desktopIsAlive === true || first.mobileIsAlive === true
    const allDown = first.desktopIsAlive === false && first.mobileIsAlive === false
    events.push({
      label: labels.firstCaptureLabel,
      date: new Date(first.createdAt),
      color: '#6366F1',
      sub: anyAlive ? 'Online' : allDown ? 'Offline' : 'Desconhecido',
      subColor: anyAlive ? '#10B981' : allDown ? '#EF4444' : '#9CA3AF',
    })
  }

  if (takedownDate) {
    events.push({
      label: labels.mitigatedLabel,
      date: takedownDate,
      color: '#10B981',
      sub: takedownDuration ? `em ${takedownDuration}` : undefined,
      subColor: '#10B981',
    })
  }

  events.sort((a, b) => a.date.getTime() - b.date.getTime())
  if (events.length < 2) return null

  return (
    <div className="mb-2 mt-4">
      <p className="mb-3 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{labels.timelineTitle}</p>
      <div className="flex items-start">
        {events.map((ev, i) => (
          <React.Fragment key={i}>
            <div className="flex min-w-[72px] flex-col items-center">
              <div className="mb-1.5 h-3 w-3 rounded-full" style={{ backgroundColor: ev.color }} />
              <p className="text-center text-[9px] font-semibold text-foreground">{ev.label}</p>
              <p className="text-center text-[8px] text-muted-foreground">{fmt(ev.date)}</p>
              {ev.sub && (
                <p className="text-center text-[8px] font-semibold" style={{ color: ev.subColor }}>{ev.sub}</p>
              )}
            </div>
            {i < events.length - 1 && (
              <div className="flex-1 px-1 pt-1.5">
                <div className="h-px bg-border" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

function DomainStatusWidget({ domainActive, imageHistory, browserMeta }: {
  domainActive: boolean
  imageHistory: HistoryEntry[]
  browserMeta?: any
}) {
  // Desktop/mobile: history takes priority, fall through to browser meta, then null
  const isDesktopActive: boolean | null =
    imageHistory[0]?.desktopIsAlive ?? browserMeta?.scenarios?.desktop?.isActive ?? null
  const isMobileActive: boolean | null =
    imageHistory[0]?.mobileIsAlive ?? browserMeta?.scenarios?.mobile?.isActive ?? null

  const hasChannelData = isDesktopActive !== null || isMobileActive !== null

  // Overall status
  let status: 'active' | 'partial' | 'inactive'
  if (hasChannelData) {
    if (isDesktopActive === true && isMobileActive === true) status = 'active'
    else if (isDesktopActive === true || isMobileActive === true) status = 'partial'
    else status = 'inactive'
  } else {
    status = domainActive ? 'active' : 'inactive'
  }

  const statusCfg = {
    active:  { label: 'Activo',              headerBg: 'bg-red-50',   border: 'border-red-100',   text: 'text-red-700',   dot: 'bg-red-500',   desc: 'Campanha acessível em todos os dispositivos' },
    partial: { label: 'Parcialmente activo', headerBg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-700', dot: 'bg-amber-500', desc: 'Campanha acessível em alguns dispositivos' },
    inactive:{ label: 'Inactivo',            headerBg: 'bg-green-50', border: 'border-green-100', text: 'text-green-700', dot: 'bg-green-500', desc: 'Campanha não acessível' },
  }[status]

  const ch = (active: boolean | null) =>
    active === true  ? { bg: 'bg-red-50 border-red-200',   icon: 'text-red-500',   badge: 'bg-red-500 text-white',    label: 'Online'  }
    : active === false ? { bg: 'bg-gray-50 border-gray-200', icon: 'text-gray-400',  badge: 'bg-gray-500 text-white',   label: 'Offline' }
    :                   { bg: 'bg-gray-50 border-gray-200', icon: 'text-gray-300',  badge: 'bg-gray-200 text-gray-500', label: '—'       }

  if (!hasChannelData) {
    // No per-channel data yet — show simple overall pill
    return (
      <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${statusCfg.headerBg} ${statusCfg.border}`}>
        <span className={`h-2 w-2 rounded-full ${statusCfg.dot}`} />
        <span className={`text-xs font-semibold ${statusCfg.text}`}>Estado da campanha: {statusCfg.label}</span>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border text-xs">
      {/* Header */}
      <div className={`flex items-center justify-between border-b border-border px-3 py-2.5 ${statusCfg.headerBg}`}>
        <div>
          <p className="font-semibold text-foreground">Estado da campanha</p>
          <p className="text-[10px] text-muted-foreground">{statusCfg.desc}</p>
        </div>
        <span className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusCfg.headerBg} ${statusCfg.border} ${statusCfg.text}`}>
          <span className={`h-2 w-2 rounded-full ${statusCfg.dot}`} />
          {statusCfg.label}
        </span>
      </div>

      {/* Per-channel rows */}
      <div className="divide-y divide-border">
        <div className={`flex items-center justify-between px-3 py-2 ${ch(isDesktopActive).bg}`}>
          <div className="flex items-center gap-2">
            <Monitor className={`h-4 w-4 ${ch(isDesktopActive).icon}`} />
            <span className="font-medium text-foreground">Desktop</span>
          </div>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${ch(isDesktopActive).badge}`}>
            {ch(isDesktopActive).label}
          </span>
        </div>
        <div className={`flex items-center justify-between px-3 py-2 ${ch(isMobileActive).bg}`}>
          <div className="flex items-center gap-2">
            <Smartphone className={`h-4 w-4 ${ch(isMobileActive).icon}`} />
            <span className="font-medium text-foreground">Mobile</span>
          </div>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${ch(isMobileActive).badge}`}>
            {ch(isMobileActive).label}
          </span>
        </div>
      </div>
    </div>
  )
}

function NetworkProbeSection({ probeData }: { probeData: ProbeData | null }) {
  const OkBadge = ({ ok }: { ok: boolean }) => (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
      ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
    }`}>
      {ok ? 'OK' : 'Falha'}
    </span>
  )

  if (!probeData) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-border bg-secondary/40 px-3 py-4">
        <p className="text-[11px] text-muted-foreground">Sem dados de sonda de rede</p>
      </div>
    )
  }

  const { run, results } = probeData
  const dns = results.find(r => r.test_type === 'dns')
  const tcp = results.find(r => r.test_type === 'tcp')
  const tls = results.find(r => r.test_type === 'tls')

  return (
    <div className="overflow-hidden rounded-lg border border-border text-xs">
      <div className="flex items-center justify-between border-b border-border bg-secondary/40 px-3 py-2">
        <div className="flex items-center gap-2">
          <Activity className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-semibold text-foreground">Network Probe</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          {run.probe_node && <span>{run.probe_node}</span>}
          <span>{fmt(run.created_at)}</span>
        </div>
      </div>

      <div className="divide-y divide-border">
        {dns && (
          <div className="px-3 py-2.5">
            <div className="flex items-center justify-between">
              <span className="font-medium text-foreground">DNS</span>
              <OkBadge ok={dns.is_alive} />
            </div>
            {(dns.raw_result?.ips?.length > 0 || dns.raw_result?.latency_ms != null) && (
              <div className="mt-1 space-y-0.5 text-[10px] text-muted-foreground">
                {dns.raw_result?.ips?.length > 0 && (
                  <p className="font-mono">{(dns.raw_result.ips as string[]).join(', ')}</p>
                )}
                {dns.raw_result?.latency_ms != null && <p>{dns.raw_result.latency_ms} ms</p>}
              </div>
            )}
          </div>
        )}

        {tcp && (
          <div className="px-3 py-2.5">
            <div className="flex items-center justify-between">
              <span className="font-medium text-foreground">TCP</span>
              <OkBadge ok={tcp.is_alive} />
            </div>
            {(tcp.raw_result?.port != null || tcp.raw_result?.latency_ms != null) && (
              <p className="mt-1 text-[10px] text-muted-foreground">
                {[
                  tcp.raw_result?.port != null ? `Porta ${tcp.raw_result.port}` : null,
                  tcp.raw_result?.latency_ms != null ? `${tcp.raw_result.latency_ms} ms` : null,
                ].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
        )}

        {tls && (
          <div className="px-3 py-2.5">
            <div className="flex items-center justify-between">
              <span className="font-medium text-foreground">TLS</span>
              <OkBadge ok={tls.is_alive} />
            </div>
            <div className="mt-1 space-y-0.5 text-[10px] text-muted-foreground">
              {tls.raw_result?.version && <p>{tls.raw_result.version}</p>}
              {tls.raw_result?.cipher && <p className="font-mono">{tls.raw_result.cipher}</p>}
              {tls.raw_result?.not_after && <p>Expira: {fmt(tls.raw_result.not_after)}</p>}
              {tls.raw_result?.issuer_cn && <p>Emissor: {tls.raw_result.issuer_cn}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── main modal ───────────────────────────────────────────────────────────────

export function ThreatDetailModal({
  entry, domainCreatedAt: domainCreatedAtProp, onClose, getToken, config = {},
}: ThreatDetailModalProps) {
  const apiBase = config.apiBase ?? ''
  const getFindings = config.getFindings ?? getCttFindings
  const labels: ThreatDetailLabels = { ...DEFAULT_LABELS, ...config.labels }

  const effectiveDomainCreatedAt = domainCreatedAtProp ?? entry?.domainCreatedAt ?? entry?.rawDomainMetadata?.createDate ?? null
  const effectiveUserEntryDomainId = entry?.userEntryDomainId ?? null

  const [screenshots, setScreenshots] = useState<ScreenshotData>({})
  const [browserMeta, setBrowserMeta] = useState<any>(null)
  const [imageHistory, setImageHistory] = useState<HistoryEntry[]>([])
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [hasScreenshots, setHasScreenshots] = useState<boolean | null>(null)
  const [screenshotError, setScreenshotError] = useState<'transient' | 'no-data' | null>(null)
  const [isArchived, setIsArchived] = useState(false)
  const [archivedDate, setArchivedDate] = useState<string | undefined>()
  const [expandedView, setExpandedView] = useState<'desktop' | 'mobile' | null>(null)
  const [showAllHistory, setShowAllHistory] = useState(false)
  const [apiDomainCreatedAt, setApiDomainCreatedAt] = useState<string | null>(null)
  const [probeData, setProbeData] = useState<ProbeData | null>(null)
  const [probeFetched, setProbeFetched] = useState(false)

  const hasFetchedRef = React.useRef(false)

  const doFetch = useCallback(async (opts: { processedEntryId?: string; entryBrowserDataId?: string } = {}) => {
    if (!entry) return
    setIsLoading(true)
    setScreenshotError(null)

    let token: string
    try {
      token = await getToken()
    } catch {
      setHasScreenshots(false)
      setScreenshotError('transient')
      setIsLoading(false)
      return
    }

    try {
      const [res, probeJson] = await Promise.all([
        fetch(`${apiBase}/api/screenshots`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            processedEntryId: opts.processedEntryId ?? entry.id,
            ...(effectiveUserEntryDomainId != null && { userEntryDomainId: effectiveUserEntryDomainId }),
            ...(opts.entryBrowserDataId != null && { entryBrowserDataId: opts.entryBrowserDataId }),
            turnstileToken: token,
          }),
        }),
        effectiveUserEntryDomainId
          ? fetch(`${apiBase}/api/probe`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ userEntryDomainId: effectiveUserEntryDomainId }),
            }).then(r => r.json()).catch(() => null)
          : Promise.resolve(null),
      ])

      if (probeJson?.success && probeJson?.data) setProbeData(probeJson.data)
      setProbeFetched(true)

      const result = await res.json()

      if (!result.success) {
        setHasScreenshots(false)
        setScreenshotError('transient')
        return
      }

      const inner = result.data?.data
      const rawHistory: HistoryEntry[] = Array.isArray(inner?.history) ? inner.history : []
      if (rawHistory.length >= 1) {
        setImageHistory(rawHistory)
        const firstWithImage = rawHistory.find(h => h.hasImage)
        if (firstWithImage) setSelectedHistoryId(firstWithImage.id)
      }

      setIsArchived(!!inner?.isArchived)
      setArchivedDate(inner?.capturedAt ?? undefined)
      if (inner?.domainMetadata?.create_date) setApiDomainCreatedAt(inner.domainMetadata.create_date)

      const shots = extractScreenshots(result.data)
      if (!shots) {
        setHasScreenshots(false)
        setScreenshotError('no-data')
        return
      }

      setScreenshots(shots)
      setBrowserMeta(inner?.browserData ?? null)
      setHasScreenshots(true)
    } catch {
      setHasScreenshots(false)
      setScreenshotError('transient')
    } finally {
      setIsLoading(false)
    }
  }, [entry, effectiveUserEntryDomainId, getToken, apiBase])

  const fetchByKey = useCallback(async (r2Key: string, histEntry: HistoryEntry) => {
    setIsLoading(true)
    setScreenshotError(null)

    let token: string
    try { token = await getToken() } catch {
      setHasScreenshots(false)
      setScreenshotError('transient')
      setIsLoading(false)
      return
    }

    try {
      const res = await fetch(`${apiBase}/api/screenshots/bykey`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ r2Key, turnstileToken: token }),
      })
      const result = await res.json()
      if (!result.success) { setHasScreenshots(false); setScreenshotError('transient'); return }

      const shots = extractScreenshots(result.data)
      if (!shots) { setHasScreenshots(false); setScreenshotError('no-data'); return }

      setScreenshots(shots)
      setBrowserMeta(histEntry.rawBrowserMetadata ?? result.data?.data?.browserData ?? null)
      setHasScreenshots(true)
    } catch {
      setHasScreenshots(false)
      setScreenshotError('transient')
    } finally {
      setIsLoading(false)
    }
  }, [getToken, apiBase])

  const handleSelectHistory = useCallback(async (id: string, r2Key: string) => {
    setSelectedHistoryId(id)
    const histEntry = imageHistory.find(h => h.id === id)!
    setBrowserMeta(histEntry?.rawBrowserMetadata ?? null)
    setScreenshots({})
    setIsArchived(false)
    setArchivedDate(undefined)
    await fetchByKey(r2Key, histEntry)
  }, [imageHistory, fetchByKey])

  useEffect(() => {
    if (!entry || hasFetchedRef.current) return
    hasFetchedRef.current = true
    doFetch()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRetry = useCallback(() => {
    hasFetchedRef.current = false
    setScreenshotError(null)
    setHasScreenshots(null)
    setScreenshots({})
    setProbeData(null)
    setProbeFetched(false)
    hasFetchedRef.current = true
    doFetch()
  }, [doFetch])

  if (!entry) return null

  // ─── derive display values from raw entry ──────────────────────────────────
  const sourceType = deriveSourceType(entry.postType)
  const channel = deriveChannel(entry.postType, entry.platformName)
  const type = entry.type ?? (!entry.isScam ? 'Outro' : sourceType === 'scall' ? 'Vishing' : channel === 'SMS' || channel === 'WhatsApp' ? 'Smishing' : 'Phishing')
  const displayTitle = entry.headline ?? entry.title ?? entry.processedInputDescription?.slice(0, 80) ?? 'Denúncia sem título'
  const domain = sourceType === 'domain'
    ? (entry.articleUrl ?? entry.rawDomainMetadata?.originalUrl ?? entry.rawDomainMetadata?.domain ?? null)
    : null
  const domainActive = entry.domainActive ?? (entry.lastMobileIsAlive === true || entry.lastDesktopIsAlive === true)
  const reporter = entry.platformName ?? 'Cidadão'
  const date = new Date(entry.createdAt).toLocaleDateString('pt-PT', { day: 'numeric', month: 'short', year: 'numeric' })
  const tags = entry.platformName ? [entry.platformName] : []
  const description = sourceType === 'scall' ? (entry.processedInputDescription ?? entry.messageContent ?? undefined) : undefined
  const messageContent = entry.messageContent ?? entry.processedInputDescription ?? ''
  const callType = sourceType === 'scall' ? 'Vishing' : null
  const sender = entry.sender ?? null

  // ─── campaign status (mirrors FraudCampaignWidget) ────────────────────────
  const isDesktopActive: boolean | null =
    imageHistory[0]?.desktopIsAlive ?? browserMeta?.scenarios?.desktop?.isActive ?? null
  const isMobileActive: boolean | null =
    imageHistory[0]?.mobileIsAlive ?? browserMeta?.scenarios?.mobile?.isActive ?? null
  const hasChannelData = isDesktopActive !== null || isMobileActive !== null
  const campaignStatus: 'active' | 'partial' | 'inactive' = hasChannelData
    ? (isDesktopActive === true && isMobileActive === true ? 'active'
      : isDesktopActive === true || isMobileActive === true ? 'partial'
      : 'inactive')
    : domainActive ? 'active' : 'inactive'

  const histAsc = [...imageHistory].reverse()
  const firstActiveIdx = histAsc.findIndex(h => h.desktopIsAlive === true || h.mobileIsAlive === true)
  const firstActiveH = firstActiveIdx >= 0 ? histAsc[firstActiveIdx] : null
  const takedownH = firstActiveH
    ? histAsc.slice(firstActiveIdx + 1).find(h => h.desktopIsAlive === false && h.mobileIsAlive === false)
    : null
  const takedownDurationLabel = takedownH && firstActiveH
    ? fmtDuration(new Date(takedownH.createdAt).getTime() - new Date(firstActiveH.createdAt).getTime())
    : null
  const lastCaptureAt = imageHistory[0]?.createdAt ?? null

  const campaignStatusCfg = {
    active:   { label: 'Activa',   bg: 'bg-red-100 text-red-700',    dot: 'bg-red-500'   },
    partial:  { label: 'Parcial',  bg: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
    inactive: { label: 'Inactiva', bg: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  }[campaignStatus]

  const findings = getFindings(entry, browserMeta)
  const displayDescription = description ?? messageContent

  const showScreenshotSection = isLoading || hasScreenshots === true || screenshotError !== null
  const desktopUrl = domain ?? 'Website'
  const mobileUrl = domain ?? 'Website'

  const visibleHistory = showAllHistory ? imageHistory : imageHistory.filter(h => h.hasImage)

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-card shadow-2xl sm:rounded-2xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-muted-foreground transition-colors hover:bg-border"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="overflow-y-auto px-6 pb-10 pt-6">
          <div className="mb-3 flex flex-wrap items-center gap-2 pr-10">
            {/* Only show campaign status once fetch has completed — avoids false "Inactiva" while loading */}
            {hasScreenshots !== null && (
              <span className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${campaignStatusCfg.bg}`}>
                <span className={`h-2 w-2 rounded-full ${campaignStatusCfg.dot}`} />
                Campanha: {campaignStatusCfg.label}
              </span>
            )}

            {takedownDurationLabel && (
              <span className="flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-700">
                <Clock className="h-2.5 w-2.5" />
                em {takedownDurationLabel}
              </span>
            )}

            {lastCaptureAt && (
              <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <Clock className="h-2.5 w-2.5" />
                {fmt(lastCaptureAt)}
              </span>
            )}

            {sourceType === 'scall' && callType && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800">
                {callType}
              </span>
            )}

            <span className="ml-auto flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Calendar className="h-3 w-3" />
              {date}
            </span>
          </div>

          <h2 className="text-xl font-semibold leading-snug text-foreground">{displayTitle}</h2>
          <p className="mb-5 mt-1 text-[11px] text-muted-foreground">
            {type} · {channel} · Reportado por {reporter} · {date}
          </p>

          <div className="mb-5 space-y-2">
            {sender && (
              <div className="flex items-center gap-2 rounded-lg bg-secondary px-3 py-2.5">
                {sourceType === 'scall'
                  ? <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  : <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                <span className="text-[11px] text-muted-foreground">Remetente:</span>
                <span className="text-[11px] font-semibold text-foreground">{sender}</span>
              </div>
            )}
            {sourceType === 'domain' && domain && (
              <div className="flex items-center gap-2 rounded-lg bg-secondary px-3 py-2.5">
                <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="text-[11px] text-muted-foreground">Domínio:</span>
                <span className="font-mono text-[11px] font-semibold text-foreground">{domain}</span>
              </div>
            )}
          </div>

          <div className="mb-6 rounded-lg bg-secondary/60 p-4">
            <p className="text-sm leading-relaxed text-foreground">{displayDescription}</p>
          </div>

          {(imageHistory.length > 0 || browserMeta) && (
            <div className="mb-4">
              <DomainStatusWidget
                domainActive={domainActive}
                imageHistory={imageHistory}
                browserMeta={browserMeta}
              />
            </div>
          )}

          {probeFetched && effectiveUserEntryDomainId && sourceType === 'domain' && (
            <div className="mb-4">
              <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Sonda de Rede</p>
              <NetworkProbeSection probeData={probeData} />
            </div>
          )}

          <CampaignTimeline
            createdAt={entry.createdAt}
            imageHistory={imageHistory}
            domainCreatedAt={effectiveDomainCreatedAt ?? apiDomainCreatedAt}
            labels={labels}
          />

          <div className="my-5 h-px bg-border" />

          <h3 className="mb-3 text-sm font-semibold text-foreground">{labels.findingsTitle}</h3>
          <div className="mb-5 space-y-2.5">
            {findings.map((f, i) => (
              <div key={i} className={`rounded-lg border-l-[3px] p-3 ${
                f.severity === 'high' ? 'border-red-500 bg-red-50' : 'border-amber-400 bg-amber-50'
              }`}>
                <div className="mb-1 flex items-center gap-2">
                  <AlertCircle className={`h-4 w-4 shrink-0 ${f.severity === 'high' ? 'text-red-600' : 'text-amber-600'}`} />
                  <span className={`text-xs font-semibold ${f.severity === 'high' ? 'text-red-800' : 'text-amber-800'}`}>
                    {f.title}
                  </span>
                </div>
                <p className={`text-xs leading-relaxed ${f.severity === 'high' ? 'text-red-700' : 'text-amber-700'}`}>
                  {f.description}
                </p>
              </div>
            ))}
          </div>

          {showScreenshotSection && (
            <div className="mb-5">
              <h3 className="mb-3 text-sm font-semibold text-foreground">{labels.screenshotsTitle}</h3>

              {isArchived && archivedDate && (
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <p className="text-xs text-amber-800">
                    Última captura disponível: {fmt(archivedDate)}
                  </p>
                </div>
              )}

              {imageHistory.length >= 1 && (
                <div className="mb-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-[10px] font-medium text-muted-foreground">Histórico:</span>
                    {imageHistory.some(h => !h.hasImage) && (
                      <button
                        onClick={() => setShowAllHistory(v => !v)}
                        className="text-[10px] text-primary hover:underline"
                      >
                        {showAllHistory
                          ? 'ocultar sem imagem'
                          : `mostrar todos (${imageHistory.length})`}
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {visibleHistory.map(h => {
                      const isSelected = selectedHistoryId === h.id
                      return (
                        <button
                          key={h.id}
                          disabled={isSelected || isLoading || !h.hasImage}
                          onClick={() => h.hasImage && handleSelectHistory(h.id, h.r2Key)}
                          className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-medium transition-colors ${
                            isSelected
                              ? 'border-primary bg-primary/10 text-primary'
                              : !h.hasImage
                                ? 'border-border bg-background text-muted-foreground opacity-50 cursor-not-allowed'
                                : 'border-border bg-background text-foreground hover:bg-secondary'
                          }`}
                        >
                          {fmt(h.createdAt, false)}
                          {!h.hasImage && <span className="ml-1 text-[8px]">sem img</span>}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {screenshotError === 'transient' && (
                <div className="flex flex-col items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
                  <AlertTriangle className="h-10 w-10 text-amber-500 stroke-[1.5]" />
                  <p className="text-sm font-semibold text-amber-800">Erro Temporário</p>
                  <p className="text-xs text-amber-700">Não foi possível carregar as capturas de ecrã neste momento.</p>
                  <button
                    onClick={handleRetry}
                    className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100"
                  >
                    <RefreshCw className="h-3 w-3" /> Tentar novamente
                  </button>
                </div>
              )}
              {screenshotError === 'no-data' && (
                <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border bg-secondary/40 p-6 text-center">
                  <ImageOff className="h-12 w-12 stroke-[1.5] text-muted-foreground" />
                  <p className="text-sm font-semibold text-muted-foreground">Capturas não disponíveis</p>
                  <p className="text-xs text-muted-foreground">Esta entrada não possui capturas de ecrã associadas.</p>
                </div>
              )}

              {!screenshotError && (
                <div className={`gap-4 ${expandedView ? 'flex flex-col' : 'flex flex-col sm:flex-row'}`}>
                  {expandedView !== 'mobile' && (
                    <div className={expandedView === 'desktop' ? 'w-full' : 'flex-1'}>
                      <BrowserWindow
                        url={desktopUrl}
                        imageSrc={screenshots.desktop}
                        deviceType="desktop"
                        isLoading={isLoading && !screenshots.desktop}
                        isExpanded={expandedView === 'desktop'}
                        onToggleExpand={() => setExpandedView(expandedView === 'desktop' ? null : 'desktop')}
                      />
                    </div>
                  )}
                  {expandedView !== 'desktop' && (
                    <div className={expandedView === 'mobile' ? 'w-full' : 'sm:w-auto'}>
                      <BrowserWindow
                        url={mobileUrl}
                        imageSrc={screenshots.mobile}
                        deviceType="mobile"
                        isLoading={isLoading && !screenshots.mobile}
                        isExpanded={expandedView === 'mobile'}
                        onToggleExpand={() => setExpandedView(expandedView === 'mobile' ? null : 'mobile')}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {tags.length > 0 && (
            <div>
              <h3 className="mb-2.5 text-sm font-semibold text-foreground">{labels.tagsTitle}</h3>
              <div className="flex flex-wrap gap-2">
                {tags.map(tag => (
                  <span key={tag} className="flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-[11px] font-medium text-muted-foreground">
                    <Tag className="h-2.5 w-2.5" />{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
